import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { setToken, clearToken, getToken, SESSION_EXPIRED_EVENT } from "lib/api";

const AuthContext = createContext(null);

// Renova o token a cada 4 min (vida de 20 min — folga generosa).
const RENEW_INTERVAL_MS = 4 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();
  const renewTimer = useRef(null);

  const stopRenewal = useCallback(() => {
    if (renewTimer.current) {
      clearInterval(renewTimer.current);
      renewTimer.current = null;
    }
  }, []);

  const renewToken = useCallback(async () => {
    if (!getToken()) return;
    try {
      const res = await api.post("/auth/refresh");
      if (res.data?.token) setToken(res.data.token);
    } catch {
      stopRenewal();
    }
  }, [stopRenewal]);

  const startRenewal = useCallback(() => {
    stopRenewal();
    renewTimer.current = setInterval(renewToken, RENEW_INTERVAL_MS);
  }, [renewToken, stopRenewal]);

  const fetchUser = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      startRenewal();
    } catch {
      setUser(null);
      clearToken();
    } finally {
      setLoading(false);
    }
  }, [startRenewal]);

  useEffect(() => {
    fetchUser();
    return stopRenewal;
  }, [fetchUser, stopRenewal]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && getToken()) renewToken();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [renewToken]);

  useEffect(() => {
    const onExpired = () => {
      stopRenewal();
      setSessionExpired(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [stopRenewal]);

  const loginUser = useCallback(async (userData, newToken) => {
    setToken(newToken);
    setUser(userData);
    setSessionExpired(false);
    startRenewal();
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      /* mantém userData do login */
    }
  }, [startRenewal]);

  const logout = useCallback(() => {
    stopRenewal();
    clearToken();
    setUser(null);
    setSessionExpired(false);
    navigate("/login");
  }, [navigate, stopRenewal]);

  return (
    <AuthContext.Provider value={{
      user, loading, loginUser, logout, fetchUser, reload: fetchUser, sessionExpired,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
